// FileViewerSplitscreenManager.jsx
// Description: This component manages the splitscreen functionality in the file viewer, allowing users to view

// Copyright HS Analysis GmbH, 2023
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React, { useState, useContext, useEffect } from "react"; // { useState, c useEffectreateContext, }
import PropTypes from "prop-types";

// MUI imports
import { Dialog } from "@mui/material";
import { DialogTitle } from "@mui/material";
import { Grid } from "@mui/material";
import { IconButton } from "@mui/material";
import { LinearProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Tooltip } from "@mui/material";

// Icon imports
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

// HSA KIT Components
import Backend from "../../common/utils/Backend";
import FileRendererDataProvider from "./FileRendererDataProvider";

//Context Imports
import { withPersistentStorage } from "../../viewer/contexts/PersistentStorageContext";
import { withTiles } from "../../viewer/contexts/TilesContext";
import { FileViewerContext } from "../FileViewer";

const useStyles = makeStyles(() => ({
  rendererGridRoot: {
    background: "#EBEBEB",
    position: "relative",
    width: "100%",
    height: "100%",
    overflowY: "inherit",
    overflowX: "inherit",
    gridTemplateColumns: " auto;",
  },
  dropfieldRoot: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.514)",
    color: "rgb(255, 255, 255)",
    zIndex: 100,
    pointerEvents: "none",
    border: "dashed white 4px",
    textAlign: "center",
    paddingTop: "calc(50vh - 74px)",
    fontSize: "20px",
  },
  splitscreenGridRoot: {
    flexGrow: 0,
    margin: 0,
    position: "relative",
    background: "#EBEBEB",
    overflow: "hidden",
  },
  activeIndicatorBorder: {
    position: "absolute",
    pointerEvents: "none",
    zIndex: 100,
    height: "100%",
    width: "100%",
    border: "solid #0673C1 4px",
  },
  layoutButtonContainer: {
    position: "absolute",
    top: 5,
    right: 5,
    zIndex: 100,
  },
  layoutButton: {
    color: "#666",
  },
}));

function FileViewerSplitscreenManager(props) {
  const FV = useContext(FileViewerContext);
  const classes = useStyles();

  const [showSplitscreenDropzone, setShowSplitscreenDropzone] = useState(false);
  const [lastFileId, setLastFileId] = useState(null);

  useEffect(() => {
    const loadedLastSplitscreenFileIds = localStorage.getItem(
      "lastSplitscreenFileIds"
    );
    if (loadedLastSplitscreenFileIds) {
      const parsedFileIds = JSON.parse(loadedLastSplitscreenFileIds);
      if (
        Array.isArray(parsedFileIds) &&
        parsedFileIds.length > 1 &&
        parsedFileIds.length !== FV.splitscreenFileIds.length
      ) {
        FV.setSplitscreenFileIds(parsedFileIds);
        FV.updateDimensions();
        setLastFileId(parsedFileIds[0]);
      }
    }
    window.addEventListener("resize", () => FV.updateDimensions());
    return () => {
      window.removeEventListener("resize", () => FV.updateDimensions());
    };
  }, []);

  useEffect(() => {
    if (FV.activeFileId === null) return;
    if (FV.activeFileId === lastFileId) return;
    if (FV.splitscreenFileIds.includes(FV.activeFileId)) return;
    handleFileChange(false, FV.activeFileId, isGuid(FV.activeFileId));
  }, [FV.activeFileId]);

  const handleFileChange = async (dropTriggered, file, idChange = false) => {
    let fileId = idChange ? file : await fetchFileId(file);
    if (lastFileId === fileId) return;
    setLastFileId(fileId);
    if (dropTriggered || FV.splitscreenFileIds.length === 0) {
      addToSplitscreen(fileId);
    } else {
      removeRenderer(FV.activeFileId);
      changeFile(fileId);
    }
    FV.setActiveFileId(fileId);
    FV.updateDimensions();
  };

  const changeFile = (newFileId) => {
    let fileIds = FV.splitscreenFileIds.slice();
    let idx = fileIds.indexOf(newFileId);

    if (idx === -1) {
      let currentIdx = fileIds.indexOf(lastFileId);
      fileIds[currentIdx] = newFileId;
      FV.setActiveFileId(newFileId);
      FV.setSplitscreenFileIds(fileIds);
      localStorage.setItem("lastSplitscreenFileIds", JSON.stringify(fileIds));
    }
  };

  /**
   * Handles the drop event for files in the splitscreen file viewer.
   * Prevents the default browser behavior, retrieves the dragged file's ID,
   * and triggers the file change handler if a valid file ID is found.
   * Also hides the splitscreen dropzone after processing.
   *
   * @param {React.DragEvent<HTMLDivElement>} e - The drag event object.
   */
  const onDropFile = (e) => {
    e.preventDefault();
    const dragFileId = e.dataTransfer.getData("fileId");
    if (!dragFileId) {
      console.warn("No fileId found in drag event.");
      return;
    }
    handleFileChange(true, dragFileId, isGuid(dragFileId));
    setShowSplitscreenDropzone(false);
  };

  const addToSplitscreen = (fileId) => {
    let fileIds = FV.splitscreenFileIds.slice();
    fileIds.push(fileId);
    FV.setSplitscreenFileIds(fileIds);
    localStorage.setItem("lastSplitscreenFileIds", JSON.stringify(fileIds));
    FV.updateDimensions();
  };

  /**
   * Handles the exclusion of a file from the splitscreen view.
   *
   * Removes the specified file from the splitscreen file IDs, updates the active file ID if necessary,
   * persists the updated splitscreen file IDs to localStorage, updates the viewer dimensions,
   * and stops the event from propagating further.
   *
   * @param {string} fileId - The ID of the file to exclude from the splitscreen.
   * @param {React.SyntheticEvent} e - The event object associated with the exclusion action.
   */
  const onExcludeSplitscreen = (fileId, e) => {
    let splitScreenFileIds = FV.splitscreenFileIds.slice();
    let activeFileId = FV.activeFileId;

    removeRenderer(fileId);
    // Remove fileId from splitScreenFileIds
    splitScreenFileIds = splitScreenFileIds.filter((id) => id !== fileId);
    FV.setSplitscreenFileIds(splitScreenFileIds);

    // if activeFileId not in splitScreenFileIds, set it to the first one
    if (!splitScreenFileIds.includes(activeFileId)) {
      activeFileId =
        splitScreenFileIds.length > 0 ? splitScreenFileIds[0] : null;
      FV.setActiveFileId(activeFileId);
      setLastFileId(activeFileId);
    }

    localStorage.setItem(
      "lastSplitscreenFileIds",
      JSON.stringify(splitScreenFileIds)
    );
    FV.updateDimensions();
    e.stopPropagation();
  };

  const removeRenderer = (fileId) => {
    let dict = FV.rendererDict;
    delete dict[fileId];
    FV.setRendererDict(dict);
  };

  const setActiveView = (currentFileId) => {
    if (FV.activeFileId === currentFileId) return;
    FV.setActiveFileId(currentFileId);
  };

  const fetchFileId = async (file) => {
    let sceneDataList = await Backend.getFileIdData(file);
    if (!sceneDataList || !("sceneIds" in sceneDataList)) return;
    let sceneZero = sceneDataList.sceneIds.find((c) => c.scene === 0);
    if (!sceneZero) return;
    return sceneZero.id;
  };

  const getSplitscreenGridLayout = () => {
    return FV.splitscreenFileIds.length === 4
      ? 6
      : FV.splitscreenFileIds.length > 2
      ? 4
      : FV.splitscreenFileIds.length > 1
      ? 6
      : 12;
  };

  const getSplitscreenGridLayoutHeight = () => {
    return FV.splitscreenFileIds.length === 4
      ? "50%"
      : FV.splitscreenFileIds.length > 6
      ? "33.333333333%"
      : FV.splitscreenFileIds.length > 3
      ? "50%"
      : "100%";
  };

  const openNextFile = () => {
    const idx = FV.sceneDataList.findIndex((obj) => obj.id === FV.activeFileId);
    const nextId =
      idx < FV.sceneDataList.length - 1
        ? FV.sceneDataList[idx + 1].id
        : FV.sceneDataList[FV.sceneDataList.length - 1].id;

    let path = null;
    Object.entries(FV.sceneList).forEach(([key, value]) => {
      if (value.some((obj) => obj.id === nextId)) path = key;
    });

    if (path) {
      FV.setActiveFileId(nextId);
      FV.setActiveFilePath(path);
    }
  };

  const openPrevFile = () => {
    const idx = FV.sceneDataList.findIndex((obj) => obj.id === FV.activeFileId);
    const prevId =
      idx - 1 >= 0 ? FV.sceneDataList[idx - 1].id : FV.sceneDataList[0].id;

    let path = null;
    Object.entries(FV.sceneList).forEach(([key, value]) => {
      if (value.some((obj) => obj.id === prevId)) path = key;
    });

    if (path) {
      FV.setActiveFileId(prevId);
      FV.setActiveFilePath(path);
    }
  };

  const isGuid = (str) => {
    var guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(str);
  };

  return (
    <div>
      <Grid
        container
        className={classes.rendererGridRoot}
        style={{
          flexBasis: "calc(100% - " + (FV.treeWidth + FV.previewWidth) + "px)",
        }}
        onDragEnter={() => {
          setShowSplitscreenDropzone(true);
        }}
        onDragLeave={() => {
          setShowSplitscreenDropzone(false);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          onDropFile(e);
        }}
      >
        {showSplitscreenDropzone && (
          <div className={classes.dropfieldRoot}>
            Drop file here for split screen.
          </div>
        )}
        {FV.splitscreenFileIds.map((currentFileId, index) => (
          <Grid
            item
            key={index}
            xs={getSplitscreenGridLayout()}
            className={classes.splitscreenGridRoot}
            style={{
              border: FV.splitscreenFileIds.length > 1 && "1px solid",
              height: getSplitscreenGridLayoutHeight(),
              zIndex: currentFileId === FV.activeFileId ? "0" : "1",
            }}
            onClick={
              FV.splitscreenFileIds.length > 1
                ? () => setActiveView(currentFileId)
                : undefined
            }
          >
            <Dialog
              open={FV.importProgress > -1 && FV.importProgress <= 100}
              disableEscapeKeyDown
              onClose={(e) => e.stopPropagation()}
              style={{ pointerEvents: "auto" }}
            >
              <DialogTitle>
                {FV.processingRotation ? "Rotating" : "Importing"} File: <br />
                &quot; {FV.activeFileName} &quot;
              </DialogTitle>
              <LinearProgress
                variant="determinate"
                value={FV.importProgress}
                style={{ height: 10 }}
              />
            </Dialog>
            <React.Fragment>
              {currentFileId === FV.activeFileId &&
                FV.splitscreenFileIds.length > 1 && (
                  <div className={classes.activeIndicatorBorder} />
                )}
              {FV.splitscreenFileIds.length > 1 && (
                <div className={classes.layoutButtonContainer}>
                  <Tooltip disableInteractive title={"Chain"}>
                    <IconButton
                      className={classes.attachButton}
                      style={{ color: "#666" }}
                      size="small"
                      onClick={(e) => FV.onChainSplitscreen(currentFileId, e)}
                    >
                      {FV.chainListFileIds.includes(currentFileId) ? (
                        <LinkIcon style={{ color: "#0673C1" }} />
                      ) : (
                        <LinkOffIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove" disableInteractive>
                    <IconButton
                      className={classes.layoutButton}
                      size="small"
                      onClick={(e) => onExcludeSplitscreen(currentFileId, e)}
                    >
                      <RemoveCircleIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              <FileRendererDataProvider
                FV={FV}
                chainListFileIds={FV.chainListFileIds}
                currentFileId={currentFileId}
                index={index}
                getMousePosition={props.getMousePosition}
                updateDimensions={FV.updateDimensions}
                openNextFile={openNextFile}
                openPrevFile={openPrevFile}
              />
            </React.Fragment>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

FileViewerSplitscreenManager.propTypes = {
  persistentStorage: PropTypes.object,
  tiles: PropTypes.object,
  getMousePosition: PropTypes.func,
};

export default withPersistentStorage(withTiles(FileViewerSplitscreenManager));
